// Equalizes tile regions within each .tile-dashboard__tiles group so every
// tile matches the tallest tile. Fills via invisible spacers only, so no
// template styles are touched; idempotent (spacers are cleared first).

const GROUP_SELECTOR = ".tile-dashboard__tiles";
const TILE_SELECTOR = ".tile__main";
const SPACER_CLASS = "tile__equalize-spacer";

const PROGRESS_SELECTOR = ".tile__progress";
const PROGRESS_BAR_SELECTOR = ".components__progress-tile-bar";

// `anchor`: fill before this element instead of after the last one, so the
// anchor (e.g. the edit link) lines up even when the region is otherwise empty.
const REGIONS = [
  { selector: ".tile__heading" },
  { selector: ".tile__progress" },
  { selector: ".tile__links, .tile__infos, .tile__actions", anchor: ".tile__target" },
  { selector: ".tile__target" },
];

function regionElements(tile, selector) {
  return Array.from(tile.querySelectorAll(selector));
}

// Relative height (last bottom - first top), so it ignores spacers above.
function regionHeight(elements) {
  if (elements.length === 0) return 0;
  const top = elements[0].getBoundingClientRect().top;
  const bottom = elements[elements.length - 1].getBoundingClientRect().bottom;
  return bottom - top;
}

function clearSpacers(group) {
  group.querySelectorAll("." + SPACER_CLASS).forEach((spacer) => spacer.remove());
}

function insertSpacer(gap, reference, position) {
  const spacer = document.createElement("div");
  spacer.className = SPACER_CLASS;
  spacer.setAttribute("aria-hidden", "true");
  spacer.style.height = gap + "px";
  reference.insertAdjacentElement(position, spacer);
}

// Aligns individual progress bars across tiles: the label above each bar (title
// plus an optional subtitle / "Unterüberschrift") varies in height, so bars at
// the same position would otherwise start at different offsets. Pad the shorter
// labels so the Nth bar lines up across every tile in the group. The spacer goes
// at the top of the compartment (not between label and bar), so the heading stays
// bottom-aligned to its bar and the extra space sits above the heading. Runs
// before the region pass so its region measurements already include these spacers.
function equalizeProgressBars(tiles) {
  const barsPerTile = tiles.map((tile) => Array.from(tile.querySelectorAll(PROGRESS_SELECTOR)));
  const maxBars = Math.max(0, ...barsPerTile.map((compartments) => compartments.length));

  for (let index = 0; index < maxBars; index += 1) {
    const entries = barsPerTile.map((compartments) => {
      const compartment = compartments[index] || null;
      const bar = compartment ? compartment.querySelector(PROGRESS_BAR_SELECTOR) : null;
      if (!bar) return { compartment: null, labelHeight: 0 };
      const labelHeight = bar.getBoundingClientRect().top - compartment.getBoundingClientRect().top;
      return { compartment, labelHeight };
    });

    const maxLabel = Math.max(0, ...entries.map((entry) => entry.labelHeight));
    entries.forEach(({ compartment, labelHeight }) => {
      if (!compartment) return;
      const gap = Math.round(maxLabel - labelHeight);
      if (gap < 1) return;
      insertSpacer(gap, compartment, "afterbegin");
    });
  }
}

function equalizeGroup(group) {
  clearSpacers(group);

  const tiles = Array.from(group.querySelectorAll(TILE_SELECTOR));
  if (tiles.length < 2) return;

  equalizeProgressBars(tiles);

  // Measure natural heights first, then insert spacers.
  const plans = REGIONS.map((region) => {
    const perTile = tiles.map((tile) => {
      const elements = regionElements(tile, region.selector);
      return {
        tile,
        last: elements.length ? elements[elements.length - 1] : null,
        height: regionHeight(elements),
      };
    });
    return { region, perTile, max: Math.max(0, ...perTile.map((entry) => entry.height)) };
  });

  plans.forEach(({ region, perTile, max }) => {
    perTile.forEach(({ tile, last, height }) => {
      const gap = Math.round(max - height);
      if (gap < 1) return;

      const anchor = region.anchor ? tile.querySelector(region.anchor) : null;
      if (!anchor && !last) return;

      if (anchor) {
        insertSpacer(gap, anchor, "beforebegin");
      } else {
        insertSpacer(gap, last, "afterend");
      }
    });
  });
}

function equalizeAll() {
  document.querySelectorAll(GROUP_SELECTOR).forEach(equalizeGroup);
}

let scheduled = false;
function scheduleEqualize() {
  if (scheduled) return;
  scheduled = true;
  window.requestAnimationFrame(() => {
    scheduled = false;
    equalizeAll();
  });
}

let started = false;

// Re-equalize whenever a group's size changes (late Vue renders, font load,
// content or viewport changes). clearSpacers + insert run synchronously in one
// frame, so the group settles to a fixed point and the observer goes quiet.
function watchGroups() {
  if (typeof window.ResizeObserver !== "function") {
    window.addEventListener("resize", scheduleEqualize);
    window.setTimeout(scheduleEqualize, 300);
    window.setTimeout(scheduleEqualize, 1000);
    return;
  }
  const observer = new window.ResizeObserver(scheduleEqualize);
  document.querySelectorAll(GROUP_SELECTOR).forEach((group) => observer.observe(group));
}

export default function () {
  scheduleEqualize();
  if (started) return;
  started = true;
  window.addEventListener("load", scheduleEqualize);
  watchGroups();
}
